import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Divider,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
  Calculate as CalculateIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

const Procurement = () => {
  const [tabValue, setTabValue] = useState(0);
  
  // Orders State
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);

  // Suppliers State
  const [suppliers, setSuppliers] = useState([]);
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierFormData, setSupplierFormData] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    gst_number: '',
    pan_number: '',
    bank_name: '',
    bank_account: '',
    ifsc_code: '',
    payment_terms: '',
    is_active: true
  });

  // Products and GST
  const [products, setProducts] = useState([]);
  const [gstRates, setGstRates] = useState([]);

  // Global Inter-State toggle
  const [isInterState, setIsInterState] = useState(false);

  const [formData, setFormData] = useState({
    supplier: '',
    po_number: '',
    expected_delivery: '',
    status: 'draft',
    notes: '',
    items: []
  });

  const ORDER_STATUSES = [
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'received', label: 'Received' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchOrders(),
        fetchSuppliers(),
        fetchProducts(),
        fetchGstRates()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await api.get('/procurement/orders/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch purchase orders');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/procurement/suppliers/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setSuppliers(data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/inventory/products/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchGstRates = async () => {
    try {
      const response = await api.get('/core/gst-rates/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setGstRates(data);
    } catch (error) {
      console.error('Error fetching GST rates:', error);
    }
  };

  // ========== SUPPLIER FUNCTIONS ==========
  
  const handleOpenSupplierDialog = (supplier = null) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setSupplierFormData({
        name: supplier.name || '',
        contact_person: supplier.contact_person || '',
        phone: supplier.phone || '',
        email: supplier.email || '',
        address: supplier.address || '',
        gst_number: supplier.gst_number || '',
        pan_number: supplier.pan_number || '',
        bank_name: supplier.bank_name || '',
        bank_account: supplier.bank_account || '',
        ifsc_code: supplier.ifsc_code || '',
        payment_terms: supplier.payment_terms || '',
        is_active: supplier.is_active !== undefined ? supplier.is_active : true
      });
    } else {
      setEditingSupplier(null);
      setSupplierFormData({
        name: '',
        contact_person: '',
        phone: '',
        email: '',
        address: '',
        gst_number: '',
        pan_number: '',
        bank_name: '',
        bank_account: '',
        ifsc_code: '',
        payment_terms: '',
        is_active: true
      });
    }
    setOpenSupplierDialog(true);
  };

  const handleCloseSupplierDialog = () => {
    setOpenSupplierDialog(false);
    setEditingSupplier(null);
  };

  const handleSupplierChange = (field) => (event) => {
    setSupplierFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSupplierSubmit = async () => {
    try {
      if (!supplierFormData.name) {
        toast.error('Supplier name is required');
        return;
      }
      if (!supplierFormData.phone) {
        toast.error('Phone number is required');
        return;
      }

      if (editingSupplier) {
        await api.put(`/procurement/suppliers/${editingSupplier.id}/`, supplierFormData);
        toast.success('Supplier updated successfully');
      } else {
        await api.post('/procurement/suppliers/', supplierFormData);
        toast.success('Supplier created successfully');
      }

      handleCloseSupplierDialog();
      fetchSuppliers();
    } catch (error) {
      console.error('Error saving supplier:', error);
      if (error.response) {
        toast.error(`Failed to save: ${JSON.stringify(error.response.data)}`);
      } else {
        toast.error('Failed to save supplier');
      }
    }
  };

  const handleSupplierDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      try {
        await api.delete(`/procurement/suppliers/${id}/`);
        toast.success('Supplier deleted successfully');
        fetchSuppliers();
      } catch (error) {
        toast.error('Failed to delete supplier');
      }
    }
  };

  // ========== ORDER FUNCTIONS ==========
  
  const getProductUOM = (product) => {
    if (!product) return 'N/A';
    if (product.uom && typeof product.uom === 'object') {
      if (product.uom.symbol) return product.uom.symbol;
    }
    if (product.uom_details && product.uom_details.symbol) {
      return product.uom_details.symbol;
    }
    if (product.uom_symbol) {
      return product.uom_symbol;
    }
    return 'N/A';
  };

  const handleOpenDialog = (order = null) => {
    if (order) {
      setEditingOrder(order);
      const hasInterState = order.items?.some(item => item.is_inter_state);
      setIsInterState(hasInterState || false);
      
      setFormData({
        supplier: order.supplier?.id || order.supplier || '',
        po_number: order.po_number || '',
        expected_delivery: order.expected_delivery || '',
        status: order.status || 'draft',
        notes: order.notes || '',
        items: order.items || []
      });
    } else {
      setEditingOrder(null);
      setIsInterState(false);
      const newPONumber = `Pur_${format(new Date(), 'ddMMyy')}_${Math.floor(Math.random() * 1000)}`;
      setFormData({
        supplier: '',
        po_number: newPONumber,
        expected_delivery: '',
        status: 'draft',
        notes: '',
        items: [createEmptyItem(false)]
      });
    }
    setOpenDialog(true);
  };

  const createEmptyItem = (interState = false) => ({
    product: '',
    product_name: '',
    uom: '',
    quantity: 0,
    unit_price: 0,
    gst_rate: 0,
    cgst_amount: 0,
    sgst_amount: 0,
    igst_amount: 0,
    total: 0,
    is_inter_state: interState
  });

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOrder(null);
    setIsInterState(false);
  };

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleInterStateToggle = (event) => {
    const newValue = event.target.checked;
    setIsInterState(newValue);
    
    setFormData(prev => {
      const newItems = prev.items.map(item => 
        calculateItemTotals({ ...item, is_inter_state: newValue })
      );
      return { ...prev, items: newItems };
    });
    
    toast.success(`GST Mode: ${newValue ? 'Inter-State (IGST)' : 'Intra-State (CGST + SGST)'}`);
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };

      if (field === 'product') {
        const selectedProduct = products.find(p => p.id === parseInt(value));
        if (selectedProduct) {
          newItems[index].product_name = selectedProduct.name;
          newItems[index].unit_price = parseFloat(selectedProduct.purchase_price) || 0;
          newItems[index].gst_rate = parseFloat(selectedProduct.gst_rate) || 0;
          newItems[index].uom = getProductUOM(selectedProduct);
        }
      }

      newItems[index] = calculateItemTotals(newItems[index]);
      
      return { ...prev, items: newItems };
    });
  };

  const calculateItemTotals = (item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unit_price) || 0;
    const gstRate = parseFloat(item.gst_rate) || 0;
    const isItemInterState = item.is_inter_state || false;
    
    const subtotal = quantity * unitPrice;
    let cgst = 0, sgst = 0, igst = 0;
    
    if (isItemInterState) {
      igst = subtotal * (gstRate / 100);
    } else {
      cgst = subtotal * (gstRate / 200);
      sgst = subtotal * (gstRate / 200);
    }
    
    const total = subtotal + cgst + sgst + igst;
    
    return {
      ...item,
      cgst_amount: cgst,
      sgst_amount: sgst,
      igst_amount: igst,
      total: total
    };
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, createEmptyItem(isInterState)]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    let subtotal = 0, totalCgst = 0, totalSgst = 0, totalIgst = 0;
    
    formData.items.forEach(item => {
      subtotal += (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      totalCgst += parseFloat(item.cgst_amount) || 0;
      totalSgst += parseFloat(item.sgst_amount) || 0;
      totalIgst += parseFloat(item.igst_amount) || 0;
    });
    
    const totalTax = totalCgst + totalSgst + totalIgst;
    const netAmount = subtotal + totalTax;
    
    return { subtotal, totalCgst, totalSgst, totalIgst, totalTax, netAmount };
  };

  const handleSubmit = async () => {
  try {
    if (!formData.supplier) {
      toast.error('Please select a supplier');
      return;
    }
    if (!formData.po_number || !formData.po_number.trim()) {
      toast.error('PO Number is required');
      return;
    }
    if (formData.items.length === 0 || !formData.items[0].product) {
      toast.error('Please add at least one product');
      return;
    }

    const totals = calculateTotals();
    
    // Build submit data - DO NOT send order_date, let backend handle it
    const submitData = {
      supplier: parseInt(formData.supplier),
      po_number: formData.po_number.trim(),
      expected_delivery: formData.expected_delivery || null,
      status: formData.status,
      notes: formData.notes || '',
      items: formData.items.map(item => ({
        product: parseInt(item.product),
        quantity: parseFloat(item.quantity) || 0,
        unit_price: parseFloat(item.unit_price) || 0,
        gst_rate: parseFloat(item.gst_rate) || 0,
        total: parseFloat(item.total) || 0,
        cgst_amount: parseFloat(item.cgst_amount) || 0,
        sgst_amount: parseFloat(item.sgst_amount) || 0,
        igst_amount: parseFloat(item.igst_amount) || 0,
        is_inter_state: item.is_inter_state || false,
      })),
      total_amount: totals.subtotal,
      tax_amount: totals.totalTax,
      net_amount: totals.netAmount,
      is_inter_state: isInterState,
    };

    console.log('=== SUBMITTING PO DATA ===');
    console.log('po_number:', submitData.po_number);
    console.log('Full data:', JSON.stringify(submitData, null, 2));

    if (editingOrder) {
      await api.put(`/procurement/orders/${editingOrder.id}/`, submitData);
      toast.success('Purchase order updated successfully');
    } else {
      await api.post('/procurement/orders/', submitData);
      toast.success('Purchase order created successfully');
    }

    handleCloseDialog();
    fetchOrders();
  } catch (error) {
    console.error('Error saving purchase order:', error);
    if (error.response) {
      console.error('Error response:', error.response.data);
      toast.error(`Failed to save: ${JSON.stringify(error.response.data)}`);
    } else {
      toast.error('Failed to save purchase order');
    }
  }
};

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this purchase order?')) {
      try {
        await api.delete(`/procurement/orders/${id}/`);
        toast.success('Purchase order deleted successfully');
        fetchOrders();
      } catch (error) {
        toast.error('Failed to delete purchase order');
      }
    }
  };

  const handleViewOrder = (order) => {
    setViewingOrder(order);
    setOpenViewDialog(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'default',
      'pending': 'warning',
      'approved': 'info',
      'received': 'success',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  };

  const totals = calculateTotals();

  const filteredSuppliers = suppliers.filter(supplier => {
    if (!supplierSearch) return true;
    const search = supplierSearch.toLowerCase();
    return (
      supplier.name?.toLowerCase().includes(search) ||
      supplier.contact_person?.toLowerCase().includes(search) ||
      supplier.phone?.toLowerCase().includes(search) ||
      supplier.gst_number?.toLowerCase().includes(search)
    );
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Procurement
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Manage suppliers and purchase orders
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {tabValue === 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Create Purchase Order
              </Button>
            )}
            {tabValue === 1 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenSupplierDialog()}
              >
                Add Supplier
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<ReceiptIcon />} iconPosition="start" label="Purchase Orders" />
          <Tab icon={<BusinessIcon />} iconPosition="start" label="Suppliers / Vendors" />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>PO Number</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Order Date</TableCell>
                    <TableCell>Expected Delivery</TableCell>
                    <TableCell>GST Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body1" color="textSecondary">
                          No purchase orders found. Click "Create Purchase Order" to add one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {order.po_number}
                          </Typography>
                        </TableCell>
                        <TableCell>{order.supplier?.name || order.supplier_name || 'N/A'}</TableCell>
                        <TableCell>
                          {order.created_at ? format(new Date(order.created_at), 'dd/MM/yyyy') : 
                           order.order_date ? format(new Date(order.order_date), 'dd/MM/yyyy') : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {order.expected_delivery ? format(new Date(order.expected_delivery), 'dd/MM/yyyy') : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={order.is_inter_state ? 'IGST' : 'CGST+SGST'}
                            color={order.is_inter_state ? 'warning' : 'info'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={order.status?.toUpperCase() || 'DRAFT'} 
                            color={getStatusColor(order.status)} 
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          ₹{parseFloat(order.net_amount || 0).toFixed(2)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            color="info"
                            onClick={() => handleViewOrder(order)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenDialog(order)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDelete(order.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Search suppliers by name, contact, phone, or GST..."
                value={supplierSearch}
                onChange={(e) => setSupplierSearch(e.target.value)}
              />
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Contact Person</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>GST Number</TableCell>
                    <TableCell>Payment Terms</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSuppliers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body1" color="textSecondary">
                          No suppliers found. Click "Add Supplier" to add one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSuppliers.map((supplier) => (
                      <TableRow key={supplier.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {supplier.name}
                          </Typography>
                        </TableCell>
                        <TableCell>{supplier.contact_person || 'N/A'}</TableCell>
                        <TableCell>{supplier.phone || 'N/A'}</TableCell>
                        <TableCell>{supplier.email || 'N/A'}</TableCell>
                        <TableCell>{supplier.gst_number || 'N/A'}</TableCell>
                        <TableCell>{supplier.payment_terms || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={supplier.is_active ? 'Active' : 'Inactive'} 
                            color={supplier.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenSupplierDialog(supplier)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleSupplierDelete(supplier.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* CREATE/EDIT PURCHASE ORDER DIALOG */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editingOrder ? 'Edit Purchase Order' : 'Create Purchase Order'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={formData.supplier}
                  onChange={handleChange('supplier')}
                  label="Supplier"
                >
                  <MenuItem value="">Select Supplier</MenuItem>
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PO Number"
                value={formData.po_number}
                onChange={handleChange('po_number')}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expected Delivery"
                type="date"
                value={formData.expected_delivery}
                onChange={handleChange('expected_delivery')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={handleChange('status')}
                  label="Status"
                >
                  {ORDER_STATUSES.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: isInterState ? '#fff3e0' : '#e8f5e9' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      GST Type
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {isInterState 
                        ? 'Inter-State (IGST) - Full GST rate applied' 
                        : 'Intra-State (CGST + SGST) - Split equally'}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isInterState}
                        onChange={handleInterStateToggle}
                        color={isInterState ? 'warning' : 'primary'}
                      />
                    }
                    label={isInterState ? 'Inter-State (IGST)' : 'Intra-State (CGST+SGST)'}
                  />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight="bold">
                  Order Items
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addItem}
                >
                  Add Item
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ minWidth: 200 }}>Product</TableCell>
                      <TableCell style={{ minWidth: 80 }}>UOM</TableCell>
                      <TableCell style={{ minWidth: 100 }}>Qty</TableCell>
                      <TableCell style={{ minWidth: 120 }}>Unit Price (₹)</TableCell>
                      <TableCell style={{ minWidth: 100 }}>GST Rate</TableCell>
                      <TableCell style={{ minWidth: 80 }}>Total</TableCell>
                      <TableCell style={{ minWidth: 60 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.items.map((item, index) => {
                      const selectedProduct = products.find(p => p.id === parseInt(item.product));
                      const uomDisplay = selectedProduct 
                        ? getProductUOM(selectedProduct) 
                        : (item.uom || 'N/A');
                      
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={item.product || ''}
                                onChange={(e) => handleItemChange(index, 'product', e.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="">Select Product</MenuItem>
                                {products.map((product) => (
                                  <MenuItem key={product.id} value={product.id}>
                                    {product.name} ({product.code})
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={uomDisplay} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={item.unit_price}
                              onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={item.gst_rate || 0}
                                onChange={(e) => handleItemChange(index, 'gst_rate', e.target.value)}
                              >
                                <MenuItem value={0}>0%</MenuItem>
                                <MenuItem value={5}>5%</MenuItem>
                                <MenuItem value={12}>12%</MenuItem>
                                <MenuItem value={18}>18%</MenuItem>
                                <MenuItem value={28}>28%</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              ₹{parseFloat(item.total || 0).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => removeItem(index)}
                              disabled={formData.items.length === 1}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ bgcolor: isInterState ? '#fff3e0' : '#e8f5e9' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    GST Calculation ({isInterState ? 'Inter-State - IGST' : 'Intra-State - CGST + SGST'})
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">Subtotal</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ₹{totals.subtotal.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">Total Tax</Typography>
                      <Typography variant="body1" fontWeight="bold" color="primary">
                        ₹{totals.totalTax.toFixed(2)}
                      </Typography>
                    </Grid>
                    {!isInterState && (
                      <>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="textSecondary">CGST</Typography>
                          <Typography variant="body1">
                            ₹{totals.totalCgst.toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="textSecondary">SGST</Typography>
                          <Typography variant="body1">
                            ₹{totals.totalSgst.toFixed(2)}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {isInterState && (
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="textSecondary">IGST</Typography>
                        <Typography variant="body1" color="warning.main" fontWeight="bold">
                          ₹{totals.totalIgst.toFixed(2)}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={isInterState ? 9 : 6}>
                      <Typography variant="body2" color="textSecondary">Net Amount (with GST)</Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        ₹{totals.netAmount.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={handleChange('notes')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
          >
            {editingOrder ? 'Update' : 'Create'} Purchase Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* SUPPLIER DIALOG */}
      <Dialog 
        open={openSupplierDialog} 
        onClose={handleCloseSupplierDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editingSupplier ? 'Edit Supplier' : 'Add Supplier'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Supplier Name"
                value={supplierFormData.name}
                onChange={handleSupplierChange('name')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Person"
                value={supplierFormData.contact_person}
                onChange={handleSupplierChange('contact_person')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={supplierFormData.phone}
                onChange={handleSupplierChange('phone')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={supplierFormData.email}
                onChange={handleSupplierChange('email')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={supplierFormData.address}
                onChange={handleSupplierChange('address')}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Tax & Bank Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GST Number"
                value={supplierFormData.gst_number}
                onChange={handleSupplierChange('gst_number')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PAN Number"
                value={supplierFormData.pan_number}
                onChange={handleSupplierChange('pan_number')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Name"
                value={supplierFormData.bank_name}
                onChange={handleSupplierChange('bank_name')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Account Number"
                value={supplierFormData.bank_account}
                onChange={handleSupplierChange('bank_account')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="IFSC Code"
                value={supplierFormData.ifsc_code}
                onChange={handleSupplierChange('ifsc_code')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Terms</InputLabel>
                <Select
                  value={supplierFormData.payment_terms}
                  onChange={handleSupplierChange('payment_terms')}
                  label="Payment Terms"
                >
                  <MenuItem value="">Select Payment Terms</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Net 15">Net 15 Days</MenuItem>
                  <MenuItem value="Net 30">Net 30 Days</MenuItem>
                  <MenuItem value="Net 45">Net 45 Days</MenuItem>
                  <MenuItem value="Net 60">Net 60 Days</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={supplierFormData.is_active}
                  onChange={handleSupplierChange('is_active')}
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSupplierDialog}>Cancel</Button>
          <Button 
            onClick={handleSupplierSubmit} 
            variant="contained"
          >
            {editingSupplier ? 'Update' : 'Add'} Supplier
          </Button>
        </DialogActions>
      </Dialog>

      {/* VIEW ORDER DIALOG */}
      <Dialog 
        open={openViewDialog} 
        onClose={() => setOpenViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Purchase Order Details
        </DialogTitle>
        <DialogContent dividers>
          {viewingOrder && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">PO Number</Typography>
                  <Typography variant="body1" fontWeight="bold">{viewingOrder.po_number}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Supplier</Typography>
                  <Typography variant="body1">{viewingOrder.supplier?.name || viewingOrder.supplier_name || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">GST Type</Typography>
                  <Chip 
                    label={viewingOrder.is_inter_state ? 'Inter-State (IGST)' : 'Intra-State (CGST+SGST)'}
                    color={viewingOrder.is_inter_state ? 'warning' : 'info'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Chip 
                    label={viewingOrder.status?.toUpperCase() || 'DRAFT'} 
                    color={getStatusColor(viewingOrder.status)} 
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Expected Delivery</Typography>
                  <Typography variant="body1">
                    {viewingOrder.expected_delivery ? format(new Date(viewingOrder.expected_delivery), 'dd/MM/yyyy') : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Order Items</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>UOM</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewingOrder.items?.map((item, index) => {
                      const product = products.find(p => p.id === item.product);
                      return (
                        <TableRow key={index}>
                          <TableCell>{product?.name || item.product_name || 'N/A'}</TableCell>
                          <TableCell>{product ? getProductUOM(product) : (item.uom_symbol || 'N/A')}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">₹{parseFloat(item.unit_price || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">₹{parseFloat(item.total || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Procurement;